import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { PgTable, PgTableColumn, PgTableBadgeCell, PgChart } from '@pgcustom/design-system';
import { logger } from '../../core/logger.js';
import type { SocietarioApi } from './../societario/societario.api.js';

const STATUS_APORTE_BADGE: Record<string, PgTableBadgeCell> = {
  confirmado: { texto: 'Confirmado', tom: 'sucesso' },
  estornado: { texto: 'Estornado', tom: 'erro' },
};

const STATUS_BONUS_BADGE: Record<string, PgTableBadgeCell> = {
  proposto: { texto: 'Proposto', tom: 'neutro' },
  aprovado: { texto: 'Aprovado', tom: 'destaque' },
  pago: { texto: 'Pago', tom: 'sucesso' },
  rejeitado: { texto: 'Rejeitado', tom: 'erro' },
  cancelado: { texto: 'Cancelado', tom: 'erro' },
};

/**
 * `<erp-dashboard-socio-page>` — visão do Sócio Investidor (referência
 * visual "fase de finalização", perfil Caio). Rota própria
 * (`#/minha-participacao`), acessível a QUALQUER usuário interno
 * autenticado — não é gateada por RBAC de papel (nenhum dos 6 papéis
 * existentes descreve "sócio investidor"), e sim pelo PRÓPRIO dado: se
 * `/societario/me` não devolver nenhum investimento, a página mostra um
 * estado vazio explícito, nunca dado fabricado — mesma garantia
 * estrutural do restante do self-service.
 *
 * "Participação de X%" NUNCA é tratada como societariamente efetivada
 * aqui — é sempre rotulada como proposta, exatamente como o domínio
 * garante (`SocietarioService`: integralizar 100% da meta nunca
 * formaliza participação sozinho).
 */
export class ErpDashboardSocioPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _societarioApi: SocietarioApi | null = null;
  private _nomeUsuario = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set societarioApi(valor: SocietarioApi) {
    this._societarioApi = valor;
  }

  set nomeUsuario(valor: string) {
    this._nomeUsuario = valor;
  }

  connectedCallback(): void {
    this.renderCarregando();
    void this.carregar();
  }

  private renderCarregando(): void {
    this.root.innerHTML = `
      <style>${this.estilosBase()}</style>
      <h1><span>Olá</span>, ${escaparHtml(primeiroNome(this._nomeUsuario))}</h1>
      <p class="sub">Sua participação e movimentações na PG Custom.</p>
      <pg-empty-state variant="carregando" titulo="Carregando"></pg-empty-state>
    `;
  }

  private estilosBase(): string {
    return `
      :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
      h1 {
        font-family: var(--pg-font-familia-display, system-ui);
        font-size: var(--pg-font-size-xl, 1.5rem);
        color: var(--pg-color-texto-primario, #f2f3f5);
        margin: 0 0 var(--pg-space-1, 4px);
      }
      h1 span { color: var(--pg-color-dourado, #c9a24b); }
      .sub { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: var(--pg-font-size-sm, 0.875rem); margin: 0 0 var(--pg-space-5, 24px); }
      .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-5, 24px); }
      .grafico-progresso { margin: var(--pg-space-2, 8px) 0 var(--pg-space-4, 16px); max-width: 420px; }
      .progresso-wrap { margin: var(--pg-space-2, 8px) 0 var(--pg-space-5, 24px); }
      .progresso-barra { height: 10px; border-radius: 999px; background: var(--pg-color-bg-hover, #22262b); overflow: hidden; }
      .progresso-preenchido { height: 100%; background: var(--pg-color-dourado, #c9a24b); }
      .progresso-legenda { display: flex; justify-content: space-between; font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: var(--pg-space-2, 8px); }
      .aviso {
        margin-top: var(--pg-space-5, 24px);
        padding: var(--pg-space-3, 12px) var(--pg-space-4, 16px);
        border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
        border-left: 3px solid var(--pg-color-dourado, #c9a24b);
        border-radius: var(--pg-radius-md, 10px);
        font-size: var(--pg-font-size-sm, 0.875rem);
        color: var(--pg-color-texto-secundario, #9aa1ab);
      }
      .secoes { display: grid; grid-template-columns: repeat(auto-fit, minmax(380px, 1fr)); gap: var(--pg-space-4, 16px); }
    `;
  }

  private async carregar(): Promise<void> {
    if (!this._societarioApi) return;
    try {
      const resumo = await this._societarioApi.meuResumo();

      if (resumo.pessoaId === null || resumo.investimentos.length === 0) {
        this.root.innerHTML = `
          <style>${this.estilosBase()}</style>
          <h1><span>Olá</span>, ${escaparHtml(primeiroNome(this._nomeUsuario))}</h1>
          <p class="sub">Sua participação e movimentações na PG Custom.</p>
          <pg-empty-state
            variant="vazio"
            titulo="Nenhuma proposta de investimento"
            descricao="Você ainda não tem uma proposta de integralização/participação societária registrada."
          ></pg-empty-state>
        `;
        return;
      }

      const investimento = resumo.investimentos[0]!;
      const participacaoProposta = resumo.participacaoAtiva?.percentual ?? null;

      this.root.innerHTML = `
        <style>${this.estilosBase()}</style>
        <h1><span>Olá</span>, ${escaparHtml(primeiroNome(this._nomeUsuario))}</h1>
        <p class="sub">Sua participação e movimentações na PG Custom.</p>

        <div class="kpis">
          <pg-kpi-card label="Meta de investimento" icone="dinheiro" estado="ok" valor="${formatBRL(centavosParaReais(investimento.investimento.valorCentavos))}"></pg-kpi-card>
          <pg-kpi-card label="Integralizado até hoje" icone="tendencia" estado="ok" valor="${formatBRL(centavosParaReais(investimento.totalIntegralizadoCentavos))}"></pg-kpi-card>
          <pg-kpi-card label="Saldo para integralizar" icone="caixa" estado="ok" valor="${formatBRL(centavosParaReais(investimento.saldoPendenteCentavos))}"></pg-kpi-card>
          <pg-kpi-card label="Participação proposta" icone="parceiros" estado="ok" valor="${participacaoProposta !== null ? `${participacaoProposta}%` : '—'}"></pg-kpi-card>
        </div>

        <div class="grafico-progresso">
          <pg-card>
            <span slot="header">Progresso da integralização</span>
            <div id="secao-grafico-progresso"></div>
          </pg-card>
        </div>

        <div class="progresso-wrap">
          <div class="progresso-barra"><div class="progresso-preenchido" style="width:${Math.min(100, investimento.percentualConcluido)}%"></div></div>
          <div class="progresso-legenda">
            <span>${investimento.percentualConcluido.toFixed(2)}% concluído</span>
            <span>Status da meta: ${escaparHtml(investimento.investimento.status)}</span>
          </div>
        </div>

        <div class="aviso">
          Este painel refere-se a uma proposta de participação societária. A participação só é efetivada após a
          conclusão da integralização <strong>e</strong> formalização societária administrativa — nunca automaticamente.
        </div>

        <br />

        <div class="secoes">
          <pg-card>
            <span slot="header">Histórico de aportes</span>
            <div id="secao-aportes"></div>
          </pg-card>
          <pg-card>
            <span slot="header">Seus cupons</span>
            <div id="secao-cupons"></div>
          </pg-card>
          <pg-card>
            <span slot="header">Seus bônus PG</span>
            <div id="secao-bonus"></div>
          </pg-card>
        </div>
      `;

      // Donut real (integralizado vs. saldo pendente) — dado já calculado
      // pelo backend (`calcularResumoIntegralizacao`), nenhuma chamada nova.
      const secaoGrafico = this.root.querySelector('#secao-grafico-progresso');
      if (secaoGrafico) {
        if (investimento.totalIntegralizadoCentavos <= 0) {
          secaoGrafico.innerHTML = `<pg-empty-state variant="vazio" titulo="Progresso da integralização" descricao="nenhum aporte confirmado ainda"></pg-empty-state>`;
        } else {
          secaoGrafico.innerHTML = '<pg-chart></pg-chart>';
          const chart = secaoGrafico.querySelector('pg-chart') as PgChart;
          chart.type = 'donut';
          chart.series = [{ key: 'valor', label: '' }];
          chart.formatarValor = (centavos: number) => formatBRL(centavosParaReais(centavos));
          chart.points = [
            { label: 'Integralizado', values: { valor: investimento.totalIntegralizadoCentavos }, color: 'var(--pg-color-dourado)' },
            {
              label: 'Saldo pendente',
              values: { valor: Math.max(0, investimento.saldoPendenteCentavos) },
              color: 'var(--pg-color-bg-hover)',
            },
          ];
        }
      }

      // `ResumoPessoalSocietario` traz o RESUMO agregado da integralização
      // (total/saldo/percentual), não a lista de aportes individuais — não
      // existe endpoint self-service para o extrato completo ainda (ver
      // pendências do relatório final).
      const secaoAportes = this.root.querySelector('#secao-aportes');
      if (secaoAportes) {
        secaoAportes.innerHTML = `<pg-empty-state variant="indisponivel" titulo="Histórico de aportes" descricao="Extrato detalhado de cada aporte ainda não está disponível no self-service — só o resumo consolidado acima (total integralizado/saldo/percentual)."></pg-empty-state>`;
      }

      this.renderTabela(
        'secao-cupons',
        [
          { key: 'codigo', label: 'Código' },
          { key: 'desconto', label: 'Desconto', align: 'right' },
          { key: 'pix', label: 'Forma exigida' },
        ],
        resumo.cupons.map((c) => ({
          codigo: c.codigo,
          desconto: `${c.descontoPercentual}%`,
          pix: c.formaPagamentoExigida === 'pix' ? 'PIX' : '—',
        })),
        'Seus cupons',
        'nenhum cupom cadastrado para você ainda'
      );

      this.renderTabela(
        'secao-bonus',
        [
          { key: 'competencia', label: 'Competência' },
          { key: 'motivo', label: 'Motivo' },
          { key: 'valor', label: 'Valor', align: 'right' },
          { key: 'status', label: 'Status' },
        ],
        resumo.bonus.map((b) => ({
          competencia: b.competencia,
          motivo: b.motivo,
          valor: formatBRL(centavosParaReais(b.valorCentavos)),
          status: STATUS_BONUS_BADGE[b.status] ?? { texto: b.status, tom: 'neutro' as const },
        })),
        'Seus bônus PG',
        'nenhum bônus PG registrado para você ainda'
      );
    } catch (erro) {
      logger.erro('falha ao carregar resumo societário (dashboard sócio)', { erro });
      this.root.innerHTML = `
        <style>${this.estilosBase()}</style>
        <h1><span>Olá</span>, ${escaparHtml(primeiroNome(this._nomeUsuario))}</h1>
        <pg-empty-state variant="erro" titulo="Falha ao carregar" descricao="não foi possível carregar seus dados. tente novamente."></pg-empty-state>
      `;
    }
  }

  private renderTabela(
    id: string,
    colunas: PgTableColumn[],
    linhas: Record<string, string | number | boolean | null | PgTableBadgeCell>[],
    titulo: string,
    mensagemVazio: string
  ): void {
    const body = this.root.querySelector(`#${id}`);
    if (!body) return;
    if (linhas.length === 0) {
      this.renderVazio(id, titulo, mensagemVazio);
      return;
    }
    body.innerHTML = '<pg-table></pg-table>';
    const tabela = body.querySelector('pg-table') as PgTable;
    tabela.columns = colunas;
    tabela.rows = linhas;
  }

  private renderVazio(id: string, titulo: string, descricao: string): void {
    const body = this.root.querySelector(`#${id}`);
    if (!body) return;
    body.innerHTML = `<pg-empty-state variant="vazio" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }
}

function primeiroNome(nomeCompleto: string): string {
  return nomeCompleto.trim().split(/\s+/)[0] ?? '';
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-dashboard-socio-page', ErpDashboardSocioPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-dashboard-socio-page': ErpDashboardSocioPage;
  }
}
